/**
 * `buildZodForRender` compiles a FormDefinition into a runtime zod schema
 * for validating submitted values.
 *
 * Used by:
 *   - the storefront renderer (zodResolver for react-hook-form)
 *   - the submit endpoint (server-side re-validation of `data` against
 *     the stored `form_versions.definition`)
 *
 * Conditional `required-if` rules are honored: a field is required only
 * if `evaluateConditions` marks it required AND it's visible given the
 * current values.
 */

import { z } from 'zod';
import type {
  CheckboxField,
  ConsentField,
  DateField,
  EmailField,
  FieldDef,
  FormDefinition,
  FormValues,
  HiddenField,
  CountryField,
  MultiselectField,
  NumberField,
  PhoneField,
  RadioField,
  RegionField,
  SelectField,
  SignatureField,
  TextField,
  TextareaField,
} from './types';
import { evaluateConditions } from './conditional';

function textValidator(field: TextField, isRequired: boolean): z.ZodType {
  let s = z.string();
  if (field.minLength != null) s = s.min(field.minLength);
  if (field.maxLength != null) s = s.max(field.maxLength);
  if (field.pattern) {
    try {
      s = s.regex(new RegExp(field.pattern));
    } catch {
      // Invalid regex in the definition is non-fatal — skip the constraint
      // rather than refusing to validate anything.
    }
  }
  return isRequired ? s.min(1, `${field.label} is required`) : s.optional().or(z.literal(''));
}

function emailValidator(field: EmailField, isRequired: boolean): z.ZodType {
  const s = z.string().email(`${field.label} must be a valid email`);
  return isRequired ? s : z.union([s, z.literal(''), z.undefined()]);
}

function phoneValidator(field: PhoneField, isRequired: boolean): z.ZodType {
  // We do basic shape-checking here; the renderer hands us E.164 from
  // PhoneInput when possible. Deeper validation (libphonenumber-js) runs
  // in the renderer + submit endpoint, not in this pure-TS compiler.
  const s = z.string();
  return isRequired ? s.min(1, `${field.label} is required`) : s.optional().or(z.literal(''));
}

function countryValidator(field: CountryField, isRequired: boolean): z.ZodType {
  // Stored value is an ISO 3166-1 alpha-2 code. We shape-check (2 chars) but
  // don't enforce the full ISO list here — @sifly/forms stays zero-dependency,
  // so the country list lives in the renderer layer, not this pure compiler.
  const s = z.string().length(2, `${field.label} is required`);
  return isRequired ? s : z.union([s, z.literal(''), z.undefined()]);
}

function regionValidator(field: RegionField, isRequired: boolean): z.ZodType {
  // State / province / region — a free-form string (a US state code, a
  // province name, etc.). The renderer narrows to a US-state dropdown when
  // the country is the US; server-side we accept any non-empty string.
  const s = z.string();
  return isRequired ? s.min(1, `${field.label} is required`) : s.optional().or(z.literal(''));
}

function numberValidator(field: NumberField, isRequired: boolean): z.ZodType {
  let n = z.number();
  if (field.min != null) n = n.min(field.min);
  if (field.max != null) n = n.max(field.max);
  // Coerce strings to numbers since HTML inputs hand back strings.
  const coerced = z.preprocess(
    (v) => (typeof v === 'string' && v !== '' ? Number(v) : v),
    n,
  );
  if (isRequired) return coerced;
  return z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    coerced.optional(),
  );
}

function textareaValidator(field: TextareaField, isRequired: boolean): z.ZodType {
  let s = z.string();
  if (field.minLength != null) s = s.min(field.minLength);
  if (field.maxLength != null) s = s.max(field.maxLength);
  return isRequired ? s.min(1, `${field.label} is required`) : s.optional().or(z.literal(''));
}

function selectValidator(field: SelectField, isRequired: boolean): z.ZodType {
  const allowed = field.options.map((o) => o.value);
  const s = z.enum(allowed as [string, ...string[]]);
  return isRequired ? s : z.union([s, z.literal(''), z.undefined()]);
}

function multiselectValidator(field: MultiselectField, isRequired: boolean): z.ZodType {
  const allowed = field.options.map((o) => o.value);
  let a = z.array(z.enum(allowed as [string, ...string[]]));
  if (field.minSelections != null) a = a.min(field.minSelections);
  if (field.maxSelections != null) a = a.max(field.maxSelections);
  return isRequired ? a.min(Math.max(1, field.minSelections ?? 1)) : a.optional();
}

function radioValidator(field: RadioField, isRequired: boolean): z.ZodType {
  const allowed = field.options.map((o) => o.value);
  const s = z.enum(allowed as [string, ...string[]]);
  return isRequired ? s : z.union([s, z.literal(''), z.undefined()]);
}

function checkboxValidator(field: CheckboxField, isRequired: boolean): z.ZodType {
  if (isRequired) {
    return z.literal(true, { message: `${field.label} must be checked` });
  }
  return z.boolean().optional();
}

function dateValidator(field: DateField, isRequired: boolean): z.ZodType {
  let s = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, `${field.label} must be a valid date`);
  if (field.min) {
    const minDate = field.min;
    s = s.refine((v) => v >= minDate, `${field.label} must be on or after ${minDate}`);
  }
  if (field.max) {
    const maxDate = field.max;
    s = s.refine((v) => v <= maxDate, `${field.label} must be on or before ${maxDate}`);
  }
  return isRequired ? s : z.union([s, z.literal(''), z.undefined()]);
}

function consentValidator(field: ConsentField, isRequired: boolean): z.ZodType {
  if (isRequired) {
    return z.literal(true, { message: `You must accept "${field.label}"` });
  }
  return z.boolean().optional();
}

/**
 * Signature value is a PNG `data:` URL drawn on a canvas. We bound it two
 * ways: the shape must be `data:image/png;base64,<base64>` (a typed-name or
 * arbitrary string can't masquerade as a signature) and the length is capped
 * at 1 MB — a 480×160 ink signature is ~5–30 KB, so 1 MB is a generous
 * ceiling that keeps the jsonb submission row lean and blocks payload abuse.
 */
const SIGNATURE_MAX_BYTES = 1_000_000;
const SIGNATURE_DATA_URL = /^data:image\/png;base64,[A-Za-z0-9+/=]+$/;

function signatureValidator(field: SignatureField, isRequired: boolean): z.ZodType {
  const png = z
    .string()
    .max(SIGNATURE_MAX_BYTES, `${field.label} signature is too large`)
    .regex(SIGNATURE_DATA_URL, `${field.label} is required`);
  return isRequired ? png : z.union([png, z.literal(''), z.undefined()]);
}

function hiddenValidator(_field: HiddenField): z.ZodType {
  return z.string().optional();
}

function validatorFor(field: FieldDef, isRequired: boolean): z.ZodType {
  switch (field.type) {
    case 'text':
      return textValidator(field, isRequired);
    case 'email':
      return emailValidator(field, isRequired);
    case 'phone':
      return phoneValidator(field, isRequired);
    case 'country':
      return countryValidator(field, isRequired);
    case 'region':
      return regionValidator(field, isRequired);
    case 'number':
      return numberValidator(field, isRequired);
    case 'textarea':
      return textareaValidator(field, isRequired);
    case 'select':
      return selectValidator(field, isRequired);
    case 'multiselect':
      return multiselectValidator(field, isRequired);
    case 'radio':
      return radioValidator(field, isRequired);
    case 'checkbox':
      return checkboxValidator(field, isRequired);
    case 'date':
      return dateValidator(field, isRequired);
    case 'consent':
      return consentValidator(field, isRequired);
    case 'signature':
      return signatureValidator(field, isRequired);
    case 'hidden':
      return hiddenValidator(field);
  }
}

/**
 * Build a flat zod object schema for the form's value shape. Conditional
 * `required` / `optional` / `hide` actions are baked in based on the
 * `currentValues` snapshot — pass the live form values so required-if
 * rules adapt to what the user has typed.
 *
 * Hidden-by-condition fields are stripped from the schema entirely so
 * they don't carry server-side validation errors when the user can't see
 * them.
 */
export function buildZodForRender(
  definition: FormDefinition,
  currentValues: FormValues = {},
): z.ZodObject<Record<string, z.ZodType>> {
  const visibility = evaluateConditions(definition, currentValues);
  const shape: Record<string, z.ZodType> = {};

  for (const field of definition.fields) {
    const vis = visibility[field.key];
    if (vis && vis.visible === false) continue;
    const required = vis?.required ?? ('required' in field ? Boolean(field.required) : false);
    shape[field.key] = validatorFor(field, required);
  }

  return z.object(shape);
}
